import React, { useState, useEffect } from 'react';
import { supabase } from '../lib/supabase';
import { useAuth } from '../context/AuthContext';
import {
  Award,
  Gift,
  RefreshCw,
  ChevronDown,
  ChevronUp,
  Sparkles,
  History,
  X,
  Check
} from 'lucide-react';
import { format } from 'date-fns';
import { es } from 'date-fns/locale';

interface PointsData {
  user_id: string;
  points_balance: number;
  points_6m: number;
  breakdown: {
    invoice_points?: number;
    credit_note_points?: number;
    production_on_time?: number;
    production_late?: number;
    sample_on_time?: number;
    sample_late?: number;
    adjustments?: number;
    redemptions?: number;
  };
  current_level: {
    id: string;
    name: string;
    min_points: number;
    max_points: number | null;
    color_primary: string;
    color_secondary: string;
    benefits: string[];
  };
  next_level?: {
    name: string;
    min_points: number;
    points_needed: number;
  };
}

interface Reward {
  id: string;
  name: string;
  description: string;
  points_cost: number;
  category: string;
  image_url?: string;
}

interface HistoryItem {
  fecha: string;
  tipo: string;
  descripcion: string;
  puntos: number;
}

const EmployeePoints: React.FC = () => {
  const { user, userRole, isAdmin } = useAuth();
  const canViewHistory = isAdmin || userRole === 'Super Admin';
  const [pointsData, setPointsData] = useState<PointsData | null>(null);
  const [rewards, setRewards] = useState<Reward[]>([]);
  const [history, setHistory] = useState<HistoryItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [historyExpanded, setHistoryExpanded] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState<string>('all');
  const [showRedemptionModal, setShowRedemptionModal] = useState(false);
  const [selectedReward, setSelectedReward] = useState<Reward | null>(null);
  const [redeeming, setRedeeming] = useState(false);
  const [resultModal, setResultModal] = useState<{ show: boolean; success: boolean; message: string }>({ show: false, success: false, message: '' });

  useEffect(() => {
    if (user?.id) {
      loadData();
    } else {
      setLoading(false);
    }
  }, [user?.id]);

  const loadData = async () => {
    if (!user?.id) return;
    setLoading(true);
    try {
      const pointsRes = await supabase.rpc('get_employee_points_breakdown', { p_user_id: user.id });
      const rewardsRes = await supabase.from('employee_rewards').select('*').eq('is_active', true).order('points_cost', { ascending: true });
      const historyRes = canViewHistory
        ? await supabase.rpc('get_employee_points_history', { p_user_id: user.id })
        : null;

      if (pointsRes.data && !pointsRes.error && !(pointsRes.data as { error?: string }).error) {
        setPointsData(pointsRes.data as PointsData);
      }

      if (rewardsRes.data) setRewards(rewardsRes.data);
      if (historyRes?.data) {
        setHistory(Array.isArray(historyRes.data) ? historyRes.data : []);
      } else if (!canViewHistory) {
        setHistory([]);
      }
    } catch (e) {
      console.error('Error loading points:', e);
    } finally {
      setLoading(false);
    }
  };

  const handleRedeem = async () => {
    if (!selectedReward || !pointsData) return;
    if (pointsData.points_balance < selectedReward.points_cost) {
      setResultModal({ show: true, success: false, message: 'No tenés suficientes puntos' });
      return;
    }
    setRedeeming(true);
    try {
      const { data, error } = await supabase.rpc('employee_redeem_reward', { p_reward_id: selectedReward.id });
      const result = data as { success: boolean; error?: string; message?: string };
      if (error) throw error;
      if (result?.success) {
        setResultModal({ show: true, success: true, message: result.message || 'Solicitud enviada. Un administrador la procesará.' });
        setShowRedemptionModal(false);
        setSelectedReward(null);
        loadData();
      } else {
        setResultModal({ show: true, success: false, message: result?.error || 'Error al canjear' });
      }
    } catch (e) {
      setResultModal({ show: true, success: false, message: e instanceof Error ? e.message : 'Error al canjear' });
    } finally {
      setRedeeming(false);
    }
  };

  const filteredRewards = selectedCategory === 'all'
    ? rewards
    : rewards.filter(r => r.category === selectedCategory);
  const categories = ['all', ...new Set(rewards.map(r => r.category).filter(Boolean))];

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <RefreshCw className="h-8 w-8 animate-spin text-indigo-500" />
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto p-6 space-y-8">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-semibold text-gray-900 dark:text-white">Mis Puntos</h1>
        <button
          onClick={loadData}
          className="p-2 rounded-lg text-gray-500 hover:bg-gray-100 dark:hover:bg-gray-800 hover:text-gray-700"
          aria-label="Actualizar"
        >
          <RefreshCw className="h-5 w-5" />
        </button>
      </div>

      {pointsData ? (
        <>
          {/* Card principal: puntos y nivel */}
          <div
            className="rounded-2xl p-6 border border-gray-200 dark:border-gray-700 overflow-hidden"
            style={{
              background: `linear-gradient(135deg, ${pointsData.current_level?.color_primary}15 0%, transparent 60%)`,
              borderColor: `${pointsData.current_level?.color_primary}40`
            }}
          >
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
              <div className="flex items-center gap-4">
                <div
                  className="w-16 h-16 rounded-2xl flex items-center justify-center"
                  style={{ backgroundColor: `${pointsData.current_level?.color_primary}25` }}
                >
                  <Award className="h-8 w-8" style={{ color: pointsData.current_level?.color_primary }} />
                </div>
                <div>
                  <p className="text-3xl font-bold text-gray-900 dark:text-white">{pointsData.points_balance}</p>
                  <p className="text-sm text-gray-500 dark:text-gray-400">puntos disponibles</p>
                  <span
                    className="inline-block mt-1 px-2 py-0.5 rounded-full text-xs font-medium"
                    style={{
                      backgroundColor: `${pointsData.current_level?.color_primary}20`,
                      color: pointsData.current_level?.color_primary
                    }}
                  >
                    {pointsData.current_level?.name || 'Sin nivel'}
                  </span>
                </div>
              </div>
              {pointsData.next_level && (
                <div className="text-sm text-gray-500 dark:text-gray-400">
                  <span className="font-medium text-gray-700 dark:text-gray-300">{pointsData.next_level.points_needed} puntos</span>
                  {' '}para {pointsData.next_level.name}
                </div>
              )}
            </div>

            {pointsData.current_level?.benefits?.length > 0 && (
              <div className="mt-4 pt-4 border-t border-gray-200/50 dark:border-gray-700/50">
                <p className="text-xs font-medium text-gray-500 dark:text-gray-400 mb-2">Beneficios de tu nivel</p>
                <ul className="flex flex-wrap gap-2">
                  {pointsData.current_level.benefits.map((b, i) => (
                    <li key={i} className="flex items-center gap-1 text-sm text-gray-700 dark:text-gray-300">
                      <Check className="h-4 w-4 text-emerald-500 shrink-0" />
                      {b}
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>

          {/* Historial colapsable — solo administradores */}
          {canViewHistory && (
          <div className="rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 overflow-hidden">
            <button
              onClick={() => setHistoryExpanded(!historyExpanded)}
              className="w-full px-4 py-3 flex items-center justify-between text-left hover:bg-gray-50 dark:hover:bg-gray-800/50 transition-colors"
            >
              <span className="flex items-center gap-2 text-sm font-medium text-gray-700 dark:text-gray-300">
                <History className="h-4 w-4" />
                Historial de puntos
              </span>
              {historyExpanded ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
            </button>
            {historyExpanded && (
              <div className="border-t border-gray-200 dark:border-gray-700 max-h-64 overflow-y-auto">
                {history.length === 0 ? (
                  <p className="p-4 text-sm text-gray-500 dark:text-gray-400">Sin movimientos aún</p>
                ) : (
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="bg-gray-50 dark:bg-gray-800/50">
                        <th className="px-4 py-2 text-left text-gray-500 dark:text-gray-400">Fecha</th>
                        <th className="px-4 py-2 text-left text-gray-500 dark:text-gray-400">Descripción</th>
                        <th className="px-4 py-2 text-right text-gray-500 dark:text-gray-400">Puntos</th>
                      </tr>
                    </thead>
                    <tbody>
                      {history.map((h, i) => (
                        <tr key={i} className="border-t border-gray-100 dark:border-gray-800">
                          <td className="px-4 py-2 text-gray-600 dark:text-gray-300">
                            {h.fecha ? format(new Date(h.fecha), 'dd/MM/yyyy HH:mm', { locale: es }) : '-'}
                          </td>
                          <td className="px-4 py-2 text-gray-600 dark:text-gray-300">{h.descripcion || h.tipo}</td>
                          <td className={`px-4 py-2 text-right font-medium ${h.puntos >= 0 ? 'text-emerald-600' : 'text-red-600'}`}>
                            {h.puntos >= 0 ? '+' : ''}{h.puntos}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </div>
            )}
          </div>
          )}

          {/* Premios canjeables */}
          <div>
            <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-4 flex items-center gap-2">
              <Gift className="h-5 w-5" />
              Premios
            </h2>

            {categories.length > 1 && (
              <div className="flex gap-2 mb-4 overflow-x-auto pb-2">
                {categories.map(cat => (
                  <button
                    key={cat}
                    onClick={() => setSelectedCategory(cat)}
                    className={`px-3 py-1.5 rounded-lg text-sm font-medium shrink-0 transition-colors ${
                      selectedCategory === cat
                        ? 'bg-indigo-100 text-indigo-700 dark:bg-indigo-900/30 dark:text-indigo-300'
                        : 'bg-gray-100 text-gray-600 dark:bg-gray-800 dark:text-gray-400 hover:bg-gray-200 dark:hover:bg-gray-700'
                    }`}
                  >
                    {cat === 'all' ? 'Todos' : cat}
                  </button>
                ))}
              </div>
            )}

            <div className="grid gap-3 grid-cols-1 sm:grid-cols-2 lg:grid-cols-3">
              {filteredRewards.map(reward => (
                <div
                  key={reward.id}
                  className="rounded-xl border border-gray-200 dark:border-gray-700 overflow-hidden hover:border-indigo-300 dark:hover:border-indigo-600 transition-colors bg-white dark:bg-gray-900"
                >
                  <div className="aspect-square bg-gray-100 dark:bg-gray-800">
                    {reward.image_url ? (
                      <img
                        src={reward.image_url}
                        alt={reward.name}
                        className="w-full h-full object-cover"
                        loading="lazy"
                      />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center text-xs text-gray-500 dark:text-gray-400">
                        Sin imagen
                      </div>
                    )}
                  </div>
                  <div className="p-4">
                  <div className="flex justify-between items-start gap-2">
                    <div>
                      <p className="font-medium text-gray-900 dark:text-white">{reward.name}</p>
                      {reward.description && (
                        <p className="text-sm text-gray-500 dark:text-gray-400 mt-0.5">{reward.description}</p>
                      )}
                    </div>
                    <div className="flex items-center gap-2 shrink-0">
                      <span className="text-lg font-semibold text-indigo-600 dark:text-indigo-400">{reward.points_cost}</span>
                      <span className="text-xs text-gray-500">pts</span>
                    </div>
                  </div>
                  <button
                    onClick={() => { setSelectedReward(reward); setShowRedemptionModal(true); }}
                    disabled={pointsData.points_balance < reward.points_cost}
                    className="mt-3 w-full py-2 rounded-lg text-sm font-medium bg-indigo-600 text-white hover:bg-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                  >
                    Canjear
                  </button>
                  </div>
                </div>
              ))}
            </div>

            {filteredRewards.length === 0 && (
              <p className="text-center py-8 text-gray-500 dark:text-gray-400">No hay premios disponibles</p>
            )}
          </div>
        </>
      ) : (
        <div className="text-center py-12 text-gray-500 dark:text-gray-400">
          <Sparkles className="h-12 w-12 mx-auto mb-4 opacity-50" />
          <p>Tu rol ({userRole}) no acumula puntos en este sistema.</p>
          <p className="text-sm mt-2">Los puntos están disponibles para Vendedores y Producción.</p>
        </div>
      )}

      {/* Modal canje */}
      {showRedemptionModal && selectedReward && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50" onClick={() => !redeeming && setShowRedemptionModal(false)}>
          <div className="bg-white dark:bg-gray-900 rounded-2xl shadow-xl max-w-md w-full p-6" onClick={e => e.stopPropagation()}>
            <div className="flex justify-between items-start mb-4">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Confirmar canje</h3>
              <button onClick={() => !redeeming && setShowRedemptionModal(false)} className="p-1 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800">
                <X className="h-5 w-5" />
              </button>
            </div>
            <p className="text-gray-600 dark:text-gray-300">
              ¿Canjear <strong>{selectedReward.points_cost} puntos</strong> por <strong>{selectedReward.name}</strong>?
            </p>
            <p className="text-sm text-gray-500 dark:text-gray-400 mt-2">
              Un administrador revisará tu solicitud y te contactará cuando esté listo.
            </p>
            <div className="mt-6 flex gap-3">
              <button
                onClick={() => !redeeming && setShowRedemptionModal(false)}
                className="flex-1 py-2 rounded-lg border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 font-medium hover:bg-gray-50 dark:hover:bg-gray-800"
              >
                Cancelar
              </button>
              <button
                onClick={handleRedeem}
                disabled={redeeming || (pointsData?.points_balance ?? 0) < selectedReward.points_cost}
                className="flex-1 py-2 rounded-lg bg-indigo-600 text-white font-medium hover:bg-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {redeeming ? 'Enviando...' : 'Confirmar'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal resultado */}
      {resultModal.show && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50" onClick={() => setResultModal(r => ({ ...r, show: false }))}>
          <div className={`rounded-2xl p-6 max-w-sm w-full ${resultModal.success ? 'bg-emerald-50 dark:bg-emerald-900/20' : 'bg-red-50 dark:bg-red-900/20'}`} onClick={e => e.stopPropagation()}>
            <p className={`font-medium ${resultModal.success ? 'text-emerald-700 dark:text-emerald-400' : 'text-red-700 dark:text-red-400'}`}>
              {resultModal.message}
            </p>
            <button
              onClick={() => setResultModal(r => ({ ...r, show: false }))}
              className="mt-4 w-full py-2 rounded-lg font-medium bg-gray-900 dark:bg-gray-100 text-white dark:text-gray-900 hover:opacity-90"
            >
              Cerrar
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default EmployeePoints;
